import { useEffect } from "react";
import styled from "styled-components";
import { currencyFormatter } from "../../helpers/formatters";
import {
  FeatureCard,
  FeatureCardTitle,
} from "../../styles/shared/card";
import { MainContent } from "../../styles/shared/drawer";
import {
  ApplicationOrderSummary,
} from "../../models/order/application-order-summary";
import LoadingSpinner from "../../components/controls/loading-spinner";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import {
  fetchOrderSummary,
  resetOrderSummary,
} from "../../store/features/ordering/order-slice";
import TableView, { ColumnConfig } from "../../components/controls/table-view";

interface OrderSummaryDrawerProps {
  orderId?: number;
}

export default function OrderSummaryDrawer({ orderId }: OrderSummaryDrawerProps) {
  const dispatch = useAppDispatch();
  const summary =
    useAppSelector((state) => state.order.orderSummary) ??
    ({ lines: [], vatBreakdown: [], net: 0, vat: 0, gross: 0 } as ApplicationOrderSummary);
  const loading = useAppSelector((state) => state.order.orderSummaryLoading);
  const errorMessage = useAppSelector((state) => state.order.orderSummaryError) ?? "";

  useEffect(() => {
    if (orderId) {
      dispatch(fetchOrderSummary(orderId));
    }

    return () => {
      dispatch(resetOrderSummary());
    };
  }, [dispatch, orderId]);

  if (loading) {
    return (
      <MainContent>
        <LoadingSpinner />
      </MainContent>
    );
  }

  if (errorMessage) {
    return (
      <MainContent>
        <FeatureCard>
          <FeatureCardTitle>Order Summary</FeatureCardTitle>
          <ErrorText>{errorMessage}</ErrorText>
        </FeatureCard>
      </MainContent>
    );
  }

  const summaryLineColumns: { [key: string]: ColumnConfig } = {
    id: { hidden: true },
    item: { name: "Item", order: 1 },
    quantity: {
      name: "Quantity",
      order: 2,
      align: "right",
      render: (value: number) => value,
    },
    price: {
      name: "Price",
      order: 3,
      align: "right",
      render: (value: number) => currencyFormatter.format(value),
    },
    netAmount: {
      name: "Net Amount",
      order: 4,
      align: "right",
      render: (value: number) => currencyFormatter.format(value),
    },
    grossAmount: {
      name: "Gross Amount",
      order: 5,
      align: "right",
      render: (value: number) => currencyFormatter.format(value),
    },
    vatRate: { hidden: true },
  };

  const vatColumns: { [key: string]: ColumnConfig } = {
    id: { hidden: true },
    vatRate: {
      name: "VAT Rate",
      order: 1,
      render: (value: number) => `${value.toFixed(1)}%`,
    },
    net: {
      name: "Net",
      order: 2,
      align: "right",
      render: (value: number) => currencyFormatter.format(value),
    },
    vat: {
      name: "VAT",
      order: 3,
      align: "right",
      render: (value: number) => currencyFormatter.format(value),
    },
    gross: {
      name: "Gross",
      order: 4,
      align: "right",
      render: (value: number) => currencyFormatter.format(value),
    },
  };

  const summaryLines = summary.lines.map((line, index) => ({
    id: `${line.item}-${index}`,
    ...line,
  }));

  const vatRows = summary.vatBreakdown.map((row, index) => ({
    id: `${row.vatRate}-${index}`,
    ...row,
  }));

  return (
    <MainContent>
      <FeatureCard>
        <TableView
          id="tblOrderSummaryLines"
          columnConfig={summaryLineColumns}
          data={summaryLines}
          totalCount={summaryLines.length}
          emptyText="No order summary lines found"
          preLoadedData
          showHeaders
          search={false}
        />

        <TotalsRow>
          <div className="total-item">
            <strong>Net</strong>
            <span>{currencyFormatter.format(summary.net)}</span>
          </div>
          <div className="total-item">
            <strong>VAT</strong>
            <span>{currencyFormatter.format(summary.vat)}</span>
          </div>
          <div className="total-item">
            <strong>Gross</strong>
            <span>{currencyFormatter.format(summary.gross)}</span>
          </div>
        </TotalsRow>
      </FeatureCard>

      {summary.vatBreakdown.length > 0 && (
        <FeatureCard>
          <TableView
            id="tblOrderSummaryVat"
            columnConfig={vatColumns}
            data={vatRows}
            totalCount={vatRows.length}
            emptyText="No VAT breakdown found"
            preLoadedData
            showHeaders
            search={false}
          />
        </FeatureCard>
      )}
    </MainContent>
  );
}

const ErrorText = styled.div`
  color: #d32f2f;
  padding: 8px 0 16px 0;
`;

const TotalsRow = styled.div`
  display: flex;
  gap: 20px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 4px;
  margin-top: 15px;

  .total-item {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;

    strong {
      font-weight: 600;
      color: #333;
    }

    span {
      margin-left: 10px;
      font-weight: 500;
      color: #1976d2;
    }
  }
`;
